import { useState } from "react";
import { formatValue, type Widget } from "../api";
import { EmptyPanel, PanelHeader } from "./LineChart";

type SortKey = "name" | "value";

export function DataTable({ widget }: { widget: Widget }) {
  const raw = Object.values(widget.series)[0] ?? [];
  // Default to the biggest values first; click a header to change it.
  const [key, setKey] = useState<SortKey>("value");
  const [descending, setDescending] = useState(true);

  if (raw.length === 0) return <EmptyPanel widget={widget} />;

  const total = raw.reduce((sum, p) => sum + (p.value ?? 0), 0);

  const sorted = [...raw].sort((a, b) => {
    const cmp =
      key === "name" ? a.label.localeCompare(b.label) : (a.value ?? 0) - (b.value ?? 0);
    return descending ? -cmp : cmp;
  });

  function sortBy(next: SortKey) {
    if (next === key) {
      setDescending((d) => !d);
    } else {
      setKey(next);
      // Names read best A→Z; numbers read best biggest-first.
      setDescending(next === "value");
    }
  }

  const arrow = (k: SortKey) => (key === k ? (descending ? " ▾" : " ▴") : "");

  return (
    <div className="panel">
      <PanelHeader widget={widget} />
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th className="sortable" onClick={() => sortBy("name")}>
                Name{arrow("name")}
              </th>
              <th className="sortable numeric" onClick={() => sortBy("value")}>
                Value{arrow("value")}
              </th>
              <th className="numeric">Share</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => (
              <tr key={p.label}>
                <td>{p.label}</td>
                <td className="numeric">{formatValue(p.value, widget.format)}</td>
                <td className="numeric muted">
                  {total !== 0 ? `${(((p.value ?? 0) / total) * 100).toFixed(1)}%` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
